/**
 * Cymatic Frequency Mappings
 * 
 * DISCLAIMER: Frequency descriptions include references to traditional spiritual beliefs
 * and ancient practices. These are presented for cultural and historical interest. Claims
 * about specific healing or therapeutic effects are not scientifically validated and should
 * not be considered medical advice. This system is for meditation, artistic exploration,
 * and spiritual practice only.
 * 
 * Real cymatic patterns are created by complex interactions of multiple frequencies,
 * not just single tones. This system uses:
 * - Multiple independent oscillators at different frequencies
 * - Frequency modulation (FM synthesis)
 * - Low-frequency oscillators (LFOs) for modulation
 * - Interference patterns from frequency combinations
 * - Standing wave patterns from resonant frequencies
 * 
 * Each pattern requires a complex frequency matrix to create the actual geometry.
 */

export interface CymaticOscillator {
  /** Frequency in Hz */
  frequency: number;
  /** Amplitude (0-1) */
  amplitude: number;
  /** Waveform type */
  waveform: OscillatorType;
  /** LFO modulation frequency (for FM) */
  lfoFrequency?: number;
  /** LFO modulation depth */
  lfoDepth?: number;
  /** Phase offset in radians */
  phase?: number;
}

export interface CymaticFrequencyConfig {
  /** Multiple oscillators that interact to create the pattern */
  oscillators: CymaticOscillator[];
  /** Carrier frequency for FM synthesis */
  carrierFrequency?: number;
  /** Modulator frequency for FM synthesis */
  modulatorFrequency?: number;
  /** FM modulation index (depth) */
  modulationIndex?: number;
  /** Description of the pattern this frequency combination creates */
  description: string;
  /** Pattern complexity level (1-10) */
  complexity: number;
}

/**
 * Cymatic frequency mappings for sacred geometry patterns
 * 
 * Each pattern uses multiple interacting frequencies to create complex interference patterns.
 * Real cymatic patterns emerge from the interaction of these frequencies, not single tones.
 */
export const CYMATIC_FREQUENCIES: Record<string, CymaticFrequencyConfig> = {
  'flower-of-life': {
    oscillators: [
      // 6-fold symmetry requires 6 primary frequencies in harmonic relationship
      { frequency: 264, amplitude: 0.8, waveform: 'sine', phase: 0 },
      { frequency: 396, amplitude: 0.7, waveform: 'sine', phase: Math.PI / 3 },
      { frequency: 528, amplitude: 0.9, waveform: 'sine', phase: Math.PI * 2 / 3 },
      { frequency: 660, amplitude: 0.6, waveform: 'sine', phase: Math.PI },
      { frequency: 792, amplitude: 0.5, waveform: 'sine', phase: Math.PI * 4 / 3 },
      { frequency: 924, amplitude: 0.4, waveform: 'sine', phase: Math.PI * 5 / 3 },
      // Additional frequencies for complexity
      { frequency: 132, amplitude: 0.3, waveform: 'triangle', lfoFrequency: 0.5, lfoDepth: 0.1 },
      { frequency: 1056, amplitude: 0.4, waveform: 'sine', phase: Math.PI / 6 }
    ],
    carrierFrequency: 528,
    modulatorFrequency: 66, // Creates 6-fold modulation
    modulationIndex: 2.5,
    description: 'Complex 6-frequency matrix creates 6-fold symmetry with 19 circles through interference patterns',
    complexity: 8
  },
  'seed-of-life': {
    oscillators: [
      // 7 circles (1 center + 6 around) requires 7 frequencies
      { frequency: 216, amplitude: 0.9, waveform: 'sine', phase: 0 },
      { frequency: 324, amplitude: 0.8, waveform: 'sine', phase: Math.PI / 6 },
      { frequency: 432, amplitude: 1.0, waveform: 'sine', phase: Math.PI / 3 },
      { frequency: 540, amplitude: 0.7, waveform: 'sine', phase: Math.PI / 2 },
      { frequency: 648, amplitude: 0.6, waveform: 'sine', phase: Math.PI * 2 / 3 },
      { frequency: 756, amplitude: 0.5, waveform: 'sine', phase: Math.PI * 5 / 6 },
      { frequency: 864, amplitude: 0.4, waveform: 'sine', phase: Math.PI }
    ],
    carrierFrequency: 432,
    modulatorFrequency: 72, // 6-fold modulation for 6 outer circles
    modulationIndex: 2.0,
    description: '7-frequency system creates center circle with 6 surrounding circles through standing wave interference',
    complexity: 7
  },
  'vesica-piscis': {
    oscillators: [
      // Two overlapping circles require two primary frequencies with interference
      { frequency: 185, amplitude: 0.9, waveform: 'sine', phase: 0 },
      { frequency: 370, amplitude: 0.9, waveform: 'sine', phase: Math.PI },
      // Interference frequencies
      { frequency: 277.5, amplitude: 0.6, waveform: 'sine', phase: Math.PI / 2 },
      { frequency: 555, amplitude: 0.5, waveform: 'sine', phase: Math.PI * 3 / 2 },
      // Modulation for vesica shape
      { frequency: 92.5, amplitude: 0.4, waveform: 'triangle', lfoFrequency: 1.0, lfoDepth: 0.15 }
    ],
    carrierFrequency: 370,
    modulatorFrequency: 185, // Creates the overlap/interference
    modulationIndex: 1.8,
    description: 'Dual-frequency interference creates the almond-shaped vesica piscis through wave cancellation and reinforcement',
    complexity: 5
  },
  'golden-spiral': {
    oscillators: [
      // Fibonacci sequence frequencies create spiral patterns
      { frequency: 161.8, amplitude: 0.8, waveform: 'sine', phase: 0 },
      { frequency: 261.8, amplitude: 0.9, waveform: 'sine', phase: Math.PI / 4 },
      { frequency: 423.6, amplitude: 0.7, waveform: 'sine', phase: Math.PI / 2 },
      { frequency: 685.4, amplitude: 0.6, waveform: 'sine', phase: Math.PI * 3 / 4 },
      { frequency: 1109, amplitude: 0.5, waveform: 'sine', phase: Math.PI },
      // Golden ratio modulation
      { frequency: 100, amplitude: 0.4, waveform: 'sine', lfoFrequency: 1.618, lfoDepth: 0.2 },
      { frequency: 161.8, amplitude: 0.3, waveform: 'triangle', lfoFrequency: 0.618, lfoDepth: 0.15 }
    ],
    carrierFrequency: 423.6,
    modulatorFrequency: 161.8, // Golden ratio modulation
    modulationIndex: 3.0,
    description: 'Fibonacci frequency sequence with golden ratio modulation creates logarithmic spiral patterns',
    complexity: 9
  },
  'metatrons-cube': {
    oscillators: [
      // 13 nodes require complex frequency matrix
      { frequency: 123.5, amplitude: 0.8, waveform: 'sine', phase: 0 },
      { frequency: 247, amplitude: 0.9, waveform: 'sine', phase: Math.PI / 13 },
      { frequency: 370.5, amplitude: 0.8, waveform: 'sine', phase: Math.PI * 2 / 13 },
      { frequency: 494, amplitude: 0.7, waveform: 'sine', phase: Math.PI * 3 / 13 },
      { frequency: 617.5, amplitude: 0.7, waveform: 'sine', phase: Math.PI * 4 / 13 },
      { frequency: 741, amplitude: 1.0, waveform: 'sine', phase: Math.PI * 5 / 13 },
      { frequency: 864.5, amplitude: 0.6, waveform: 'sine', phase: Math.PI * 6 / 13 },
      { frequency: 988, amplitude: 0.6, waveform: 'sine', phase: Math.PI * 7 / 13 },
      { frequency: 1111.5, amplitude: 0.5, waveform: 'sine', phase: Math.PI * 8 / 13 },
      { frequency: 1235, amplitude: 0.5, waveform: 'sine', phase: Math.PI * 9 / 13 },
      { frequency: 1358.5, amplitude: 0.4, waveform: 'sine', phase: Math.PI * 10 / 13 },
      { frequency: 1482, amplitude: 0.4, waveform: 'sine', phase: Math.PI * 11 / 13 },
      { frequency: 1605.5, amplitude: 0.3, waveform: 'sine', phase: Math.PI * 12 / 13 }
    ],
    carrierFrequency: 741,
    modulatorFrequency: 57, // Creates 13-fold modulation
    modulationIndex: 2.8,
    description: '13-frequency matrix creates 13 nodal points with 78 interconnections through complex wave interference',
    complexity: 10
  },
  'sri-yantra': {
    oscillators: [
      // 9 triangles + 16 petals + bindu requires many frequencies
      // 4 upward triangles (Shiva)
      { frequency: 213, amplitude: 0.8, waveform: 'sine', phase: 0 },
      { frequency: 426, amplitude: 0.9, waveform: 'sine', phase: Math.PI / 4 },
      { frequency: 639, amplitude: 0.7, waveform: 'sine', phase: Math.PI / 2 },
      { frequency: 852, amplitude: 1.0, waveform: 'sine', phase: Math.PI * 3 / 4 },
      // 5 downward triangles (Shakti)
      { frequency: 340.8, amplitude: 0.7, waveform: 'sine', phase: Math.PI },
      { frequency: 511.2, amplitude: 0.6, waveform: 'sine', phase: Math.PI * 5 / 4 },
      { frequency: 681.6, amplitude: 0.6, waveform: 'sine', phase: Math.PI * 3 / 2 },
      { frequency: 852, amplitude: 0.5, waveform: 'sine', phase: Math.PI * 7 / 4 },
      { frequency: 1022.4, amplitude: 0.4, waveform: 'sine', phase: Math.PI * 2 },
      // 16-petal lotus frequencies
      { frequency: 106.5, amplitude: 0.4, waveform: 'triangle', lfoFrequency: 2.0, lfoDepth: 0.1 },
      { frequency: 1704, amplitude: 0.3, waveform: 'sine', phase: Math.PI / 16 }
    ],
    carrierFrequency: 852,
    modulatorFrequency: 53.25, // Creates 16-fold modulation for petals
    modulationIndex: 3.2,
    description: 'Complex 12-frequency system with triangular and circular interference creates 9 interlocking triangles and 16-petal lotus',
    complexity: 10
  },
  'hexagon': {
    oscillators: [
      // 6-fold symmetry creates hexagonal patterns
      { frequency: 220, amplitude: 0.9, waveform: 'sine', phase: 0 },
      { frequency: 330, amplitude: 0.8, waveform: 'sine', phase: Math.PI / 3 },
      { frequency: 440, amplitude: 1.0, waveform: 'sine', phase: Math.PI * 2 / 3 },
      { frequency: 550, amplitude: 0.7, waveform: 'sine', phase: Math.PI },
      { frequency: 660, amplitude: 0.6, waveform: 'sine', phase: Math.PI * 4 / 3 },
      { frequency: 770, amplitude: 0.5, waveform: 'sine', phase: Math.PI * 5 / 3 },
      // Additional harmonics for complexity
      { frequency: 110, amplitude: 0.4, waveform: 'triangle', lfoFrequency: 0.3, lfoDepth: 0.1 },
      { frequency: 880, amplitude: 0.3, waveform: 'sine', phase: Math.PI / 6 }
    ],
    carrierFrequency: 440,
    modulatorFrequency: 73.33, // Creates 6-fold modulation
    modulationIndex: 2.2,
    description: '6-frequency matrix creates perfect hexagonal patterns through 6-fold wave interference',
    complexity: 7
  },
  'pentagon': {
    oscillators: [
      // 5-fold symmetry creates pentagonal patterns
      { frequency: 200, amplitude: 0.9, waveform: 'sine', phase: 0 },
      { frequency: 300, amplitude: 0.8, waveform: 'sine', phase: Math.PI * 2 / 5 },
      { frequency: 400, amplitude: 1.0, waveform: 'sine', phase: Math.PI * 4 / 5 },
      { frequency: 500, amplitude: 0.7, waveform: 'sine', phase: Math.PI * 6 / 5 },
      { frequency: 600, amplitude: 0.6, waveform: 'sine', phase: Math.PI * 8 / 5 },
      // Golden ratio frequencies enhance pentagon
      { frequency: 323.6, amplitude: 0.5, waveform: 'sine', phase: Math.PI / 5 },
      { frequency: 100, amplitude: 0.4, waveform: 'triangle', lfoFrequency: 0.4, lfoDepth: 0.12 }
    ],
    carrierFrequency: 400,
    modulatorFrequency: 80, // Creates 5-fold modulation
    modulationIndex: 2.4,
    description: '7-frequency system creates pentagonal patterns through 5-fold symmetry and golden ratio harmonics',
    complexity: 8
  },
  'torus': {
    oscillators: [
      // Torus requires 3D wave interference patterns
      { frequency: 150, amplitude: 0.9, waveform: 'sine', phase: 0 },
      { frequency: 225, amplitude: 0.8, waveform: 'sine', phase: Math.PI / 4 },
      { frequency: 300, amplitude: 1.0, waveform: 'sine', phase: Math.PI / 2 },
      { frequency: 375, amplitude: 0.7, waveform: 'sine', phase: Math.PI * 3 / 4 },
      { frequency: 450, amplitude: 0.6, waveform: 'sine', phase: Math.PI },
      { frequency: 525, amplitude: 0.5, waveform: 'sine', phase: Math.PI * 5 / 4 },
      { frequency: 600, amplitude: 0.4, waveform: 'sine', phase: Math.PI * 3 / 2 },
      // Rotational frequencies for torus shape
      { frequency: 75, amplitude: 0.4, waveform: 'triangle', lfoFrequency: 0.25, lfoDepth: 0.15 },
      { frequency: 750, amplitude: 0.3, waveform: 'sine', phase: Math.PI * 7 / 4 }
    ],
    carrierFrequency: 300,
    modulatorFrequency: 50, // Creates rotational modulation
    modulationIndex: 2.6,
    description: '9-frequency matrix creates toroidal patterns through 3D wave interference and rotational modulation',
    complexity: 9
  },
  'mandala': {
    oscillators: [
      // Mandala requires radial symmetry with multiple layers
      { frequency: 180, amplitude: 0.9, waveform: 'sine', phase: 0 },
      { frequency: 270, amplitude: 0.8, waveform: 'sine', phase: Math.PI / 8 },
      { frequency: 360, amplitude: 1.0, waveform: 'sine', phase: Math.PI / 4 },
      { frequency: 450, amplitude: 0.7, waveform: 'sine', phase: Math.PI * 3 / 8 },
      { frequency: 540, amplitude: 0.6, waveform: 'sine', phase: Math.PI / 2 },
      { frequency: 630, amplitude: 0.5, waveform: 'sine', phase: Math.PI * 5 / 8 },
      { frequency: 720, amplitude: 0.4, waveform: 'sine', phase: Math.PI * 3 / 4 },
      { frequency: 810, amplitude: 0.3, waveform: 'sine', phase: Math.PI * 7 / 8 },
      // Radial layer frequencies
      { frequency: 90, amplitude: 0.4, waveform: 'triangle', lfoFrequency: 0.2, lfoDepth: 0.1 },
      { frequency: 900, amplitude: 0.3, waveform: 'sine', phase: Math.PI }
    ],
    carrierFrequency: 360,
    modulatorFrequency: 45, // Creates 8-fold radial modulation
    modulationIndex: 2.8,
    description: '10-frequency system creates mandala patterns with 8-fold radial symmetry through layered wave interference',
    complexity: 9
  },
  'chladni-hexagon': {
    oscillators: [
      // Classic Chladni figure - hexagonal nodal patterns
      { frequency: 250, amplitude: 0.9, waveform: 'sine', phase: 0 },
      { frequency: 375, amplitude: 0.8, waveform: 'sine', phase: Math.PI / 6 },
      { frequency: 500, amplitude: 1.0, waveform: 'sine', phase: Math.PI / 3 },
      { frequency: 625, amplitude: 0.7, waveform: 'sine', phase: Math.PI / 2 },
      { frequency: 750, amplitude: 0.6, waveform: 'sine', phase: Math.PI * 2 / 3 },
      { frequency: 875, amplitude: 0.5, waveform: 'sine', phase: Math.PI * 5 / 6 },
      { frequency: 1000, amplitude: 0.4, waveform: 'sine', phase: Math.PI },
      // Nodal point frequencies
      { frequency: 125, amplitude: 0.4, waveform: 'triangle', lfoFrequency: 0.35, lfoDepth: 0.12 },
      { frequency: 1125, amplitude: 0.3, waveform: 'sine', phase: Math.PI * 7 / 6 }
    ],
    carrierFrequency: 500,
    modulatorFrequency: 83.33, // Creates 6-fold nodal pattern
    modulationIndex: 2.3,
    description: '9-frequency Chladni pattern creates hexagonal nodal lines through standing wave interference on a plate',
    complexity: 8
  },
  'star-pentagram': {
    oscillators: [
      // 5-pointed star requires 5-fold symmetry with golden ratio
      { frequency: 174, amplitude: 0.9, waveform: 'sine', phase: 0 },
      { frequency: 261, amplitude: 0.8, waveform: 'sine', phase: Math.PI * 2 / 5 },
      { frequency: 348, amplitude: 1.0, waveform: 'sine', phase: Math.PI * 4 / 5 },
      { frequency: 435, amplitude: 0.7, waveform: 'sine', phase: Math.PI * 6 / 5 },
      { frequency: 522, amplitude: 0.6, waveform: 'sine', phase: Math.PI * 8 / 5 },
      // Golden ratio harmonics for star points (174 * 1.618 = 281.532)
      { frequency: 281.5, amplitude: 0.5, waveform: 'sine', phase: Math.PI / 5 },
      { frequency: 87, amplitude: 0.4, waveform: 'triangle', lfoFrequency: 0.38, lfoDepth: 0.13 }
    ],
    carrierFrequency: 348,
    modulatorFrequency: 69.6, // Creates 5-fold star modulation
    modulationIndex: 2.5,
    description: '7-frequency system creates 5-pointed star patterns through golden ratio harmonics and 5-fold symmetry',
    complexity: 8
  },
  'spiral-galaxy': {
    oscillators: [
      // Spiral galaxy requires logarithmic frequency progression
      { frequency: 100, amplitude: 0.9, waveform: 'sine', phase: 0 },
      { frequency: 161.8, amplitude: 0.8, waveform: 'sine', phase: Math.PI / 8 },
      { frequency: 261.8, amplitude: 1.0, waveform: 'sine', phase: Math.PI / 4 },
      { frequency: 423.6, amplitude: 0.7, waveform: 'sine', phase: Math.PI * 3 / 8 },
      { frequency: 685.4, amplitude: 0.6, waveform: 'sine', phase: Math.PI / 2 },
      { frequency: 1109, amplitude: 0.5, waveform: 'sine', phase: Math.PI * 5 / 8 },
      // Rotational frequencies
      { frequency: 50, amplitude: 0.4, waveform: 'triangle', lfoFrequency: 0.15, lfoDepth: 0.18 },
      { frequency: 1794, amplitude: 0.3, waveform: 'sine', phase: Math.PI * 3 / 4 }
    ],
    carrierFrequency: 423.6,
    modulatorFrequency: 65.4, // Creates spiral rotation
    modulationIndex: 3.1,
    description: '8-frequency logarithmic progression creates spiral galaxy patterns through golden ratio frequency scaling',
    complexity: 9
  },
  'tree-of-life': {
    oscillators: [
      // Tree of Life: 10 Sephiroth frequencies based on traditional correspondences
      // Each sephirah has traditional planetary/musical note associations
      // Frequencies calculated from A4=440Hz using just intonation and sacred ratios
      
      // 0: Kether (Crown) - Primum Mobile, highest vibration, pure unity
      { frequency: 528, amplitude: 1.0, waveform: 'sine', phase: 0 }, // Love frequency, highest sephirah
      
      // 1: Chokmah (Wisdom) - Zodiac, masculine principle, active force
      { frequency: 432, amplitude: 0.9, waveform: 'sine', phase: Math.PI / 10 }, // Natural tuning, cosmic harmony
      
      // 2: Binah (Understanding) - Saturn, feminine principle, form
      { frequency: 396, amplitude: 0.85, waveform: 'sine', phase: Math.PI * 2 / 10 }, // Liberation, breaking limitations
      
      // 3: Chesed (Mercy) - Jupiter, expansion, benevolence
      { frequency: 639, amplitude: 0.8, waveform: 'sine', phase: Math.PI * 3 / 10 }, // Connection, heart chakra
      
      // 4: Geburah (Severity) - Mars, contraction, discipline
      { frequency: 741, amplitude: 0.75, waveform: 'sine', phase: Math.PI * 4 / 10 }, // Expression, throat chakra
      
      // 5: Tiphareth (Beauty) - Sun, balance, harmony (central sephirah)
      { frequency: 528, amplitude: 1.0, waveform: 'sine', phase: Math.PI * 5 / 10 }, // Love frequency, heart center
      
      // 6: Netzach (Victory) - Venus, emotion, nature
      { frequency: 417, amplitude: 0.7, waveform: 'sine', phase: Math.PI * 6 / 10 }, // Facilitating change, sacral
      
      // 7: Hod (Glory) - Mercury, intellect, communication
      { frequency: 741, amplitude: 0.7, waveform: 'sine', phase: Math.PI * 7 / 10 }, // Expression, communication
      
      // 8: Yesod (Foundation) - Moon, astral plane, foundation
      { frequency: 396, amplitude: 0.65, waveform: 'sine', phase: Math.PI * 8 / 10 }, // Root foundation, liberation
      
      // 9: Malkuth (Kingdom) - Earth, physical manifestation, material world
      { frequency: 256, amplitude: 0.6, waveform: 'sine', phase: Math.PI * 9 / 10 }, // Middle C, grounding frequency
      
      // 22 Paths: Represented by modulating frequencies
      // Path frequencies use golden ratio and Fibonacci relationships
      { frequency: 55.5, amplitude: 0.3, waveform: 'triangle', lfoFrequency: 0.22, lfoDepth: 0.1 }, // Path modulation base
      { frequency: 89.9, amplitude: 0.25, waveform: 'sine', phase: Math.PI / 22, lfoFrequency: 0.11, lfoDepth: 0.05 }, // Fibonacci path frequency
    ],
    carrierFrequency: 528, // Tiphareth (Beauty) as central carrier - represents balance
    modulatorFrequency: 55.5, // 22 paths modulation (22 * 2.5 = 55)
    modulationIndex: 2.618, // Golden ratio modulation
    description: 'Authentic Tree of Life frequencies: 10 sephiroth with traditional correspondences (Kether=528Hz, Chokmah=432Hz, Binah=396Hz, etc.) plus 22 path frequencies representing Hebrew letters. Central carrier at Tiphareth (528Hz) represents balance and beauty.',
    complexity: 10
  },
  
  // ============================================================================
  // CLASSIC CHLADNI PATTERNS - Scientific Cymatic Figures
  // ============================================================================
  
  'chladni-1-1': {
    oscillators: [
      // First fundamental mode (1,1) - simplest nodal pattern
      { frequency: 150, amplitude: 1.0, waveform: 'sine', phase: 0 },
      { frequency: 300, amplitude: 0.8, waveform: 'sine', phase: Math.PI / 2 },
      { frequency: 450, amplitude: 0.6, waveform: 'sine', phase: Math.PI }
    ],
    carrierFrequency: 150,
    modulatorFrequency: 25,
    modulationIndex: 1.5,
    description: 'Chladni pattern (1,1): Fundamental mode with single nodal line creating simple division pattern',
    complexity: 3
  },
  
  'chladni-2-2': {
    oscillators: [
      // Second mode (2,2) - four quadrant pattern
      { frequency: 300, amplitude: 1.0, waveform: 'sine', phase: 0 },
      { frequency: 600, amplitude: 0.9, waveform: 'sine', phase: Math.PI / 4 },
      { frequency: 900, amplitude: 0.7, waveform: 'sine', phase: Math.PI / 2 },
      { frequency: 1200, amplitude: 0.5, waveform: 'sine', phase: Math.PI * 3 / 4 }
    ],
    carrierFrequency: 300,
    modulatorFrequency: 50,
    modulationIndex: 2.0,
    description: 'Chladni pattern (2,2): Four-quadrant nodal pattern with crossing nodal lines',
    complexity: 5
  },
  
  'chladni-3-3': {
    oscillators: [
      // Third mode (3,3) - nine-section pattern
      { frequency: 450, amplitude: 1.0, waveform: 'sine', phase: 0 },
      { frequency: 675, amplitude: 0.9, waveform: 'sine', phase: Math.PI / 6 },
      { frequency: 900, amplitude: 0.8, waveform: 'sine', phase: Math.PI / 3 },
      { frequency: 1125, amplitude: 0.7, waveform: 'sine', phase: Math.PI / 2 },
      { frequency: 1350, amplitude: 0.6, waveform: 'sine', phase: Math.PI * 2 / 3 },
      { frequency: 1575, amplitude: 0.5, waveform: 'sine', phase: Math.PI * 5 / 6 }
    ],
    carrierFrequency: 450,
    modulatorFrequency: 75,
    modulationIndex: 2.5,
    description: 'Chladni pattern (3,3): Nine-section nodal pattern with complex interference',
    complexity: 7
  },
  
  'chladni-radial': {
    oscillators: [
      // Radial Chladni pattern - circular nodal lines
      { frequency: 200, amplitude: 1.0, waveform: 'sine', phase: 0 },
      { frequency: 400, amplitude: 0.9, waveform: 'sine', phase: Math.PI / 8 },
      { frequency: 600, amplitude: 0.8, waveform: 'sine', phase: Math.PI / 4 },
      { frequency: 800, amplitude: 0.7, waveform: 'sine', phase: Math.PI * 3 / 8 },
      { frequency: 1000, amplitude: 0.6, waveform: 'sine', phase: Math.PI / 2 },
      { frequency: 1200, amplitude: 0.5, waveform: 'sine', phase: Math.PI * 5 / 8 }
    ],
    carrierFrequency: 200,
    modulatorFrequency: 33.33,
    modulationIndex: 2.2,
    description: 'Radial Chladni pattern: Concentric circular nodal lines creating ring patterns',
    complexity: 6
  },
  
  // ============================================================================
  // SOLFEGGIO FREQUENCIES - Ancient Healing Tones
  // ============================================================================
  
  'solfeggio-174': {
    oscillators: [
      // 174 Hz - Foundation, grounding, pain relief
      { frequency: 174, amplitude: 1.0, waveform: 'sine', phase: 0 },
      { frequency: 348, amplitude: 0.8, waveform: 'sine', phase: Math.PI / 3 },
      { frequency: 522, amplitude: 0.6, waveform: 'sine', phase: Math.PI * 2 / 3 },
      { frequency: 696, amplitude: 0.4, waveform: 'sine', phase: Math.PI }
    ],
    carrierFrequency: 174,
    modulatorFrequency: 29,
    modulationIndex: 1.8,
    description: 'Solfeggio 174 Hz: Foundation frequency for grounding, pain relief, and sense of security',
    complexity: 4
  },
  
  'solfeggio-285': {
    oscillators: [
      // 285 Hz - Energy fields and grounding
      { frequency: 285, amplitude: 1.0, waveform: 'sine', phase: 0 },
      { frequency: 570, amplitude: 0.8, waveform: 'sine', phase: Math.PI / 4 },
      { frequency: 855, amplitude: 0.6, waveform: 'sine', phase: Math.PI / 2 },
      { frequency: 1140, amplitude: 0.4, waveform: 'sine', phase: Math.PI * 3 / 4 }
    ],
    carrierFrequency: 285,
    modulatorFrequency: 47.5,
    modulationIndex: 2.0,
    description: 'Solfeggio 285 Hz: Grounding energy, restful meditation, and field awareness (traditional)',
    complexity: 5
  },
  
  'solfeggio-417': {
    oscillators: [
      // 417 Hz - Facilitating change, undoing situations
      { frequency: 417, amplitude: 1.0, waveform: 'sine', phase: 0 },
      { frequency: 834, amplitude: 0.9, waveform: 'sine', phase: Math.PI / 5 },
      { frequency: 1251, amplitude: 0.7, waveform: 'sine', phase: Math.PI * 2 / 5 },
      { frequency: 1668, amplitude: 0.5, waveform: 'sine', phase: Math.PI * 3 / 5 },
      { frequency: 2085, amplitude: 0.3, waveform: 'sine', phase: Math.PI * 4 / 5 }
    ],
    carrierFrequency: 417,
    modulatorFrequency: 69.5,
    modulationIndex: 2.2,
    description: 'Solfeggio 417 Hz: Facilitating change, undoing situations, and breaking negative patterns',
    complexity: 6
  },
  
  'solfeggio-528': {
    oscillators: [
      // 528 Hz - Love frequency, transformation
      { frequency: 528, amplitude: 1.0, waveform: 'sine', phase: 0 },
      { frequency: 1056, amplitude: 0.9, waveform: 'sine', phase: Math.PI / 6 },
      { frequency: 1584, amplitude: 0.7, waveform: 'sine', phase: Math.PI / 3 },
      { frequency: 2112, amplitude: 0.5, waveform: 'sine', phase: Math.PI / 2 },
      { frequency: 2640, amplitude: 0.3, waveform: 'sine', phase: Math.PI * 2 / 3 }
    ],
    carrierFrequency: 528,
    modulatorFrequency: 88,
    modulationIndex: 2.5,
    description: 'Solfeggio 528 Hz: Love frequency, transformation, and positive intention (traditional)',
    complexity: 7
  },
  
  'solfeggio-639': {
    oscillators: [
      // 639 Hz - Connection, relationships, harmony
      { frequency: 639, amplitude: 1.0, waveform: 'sine', phase: 0 },
      { frequency: 1278, amplitude: 0.9, waveform: 'sine', phase: Math.PI / 7 },
      { frequency: 1917, amplitude: 0.7, waveform: 'sine', phase: Math.PI * 2 / 7 },
      { frequency: 2556, amplitude: 0.5, waveform: 'sine', phase: Math.PI * 3 / 7 },
      { frequency: 3195, amplitude: 0.3, waveform: 'sine', phase: Math.PI * 4 / 7 }
    ],
    carrierFrequency: 639,
    modulatorFrequency: 106.5,
    modulationIndex: 2.3,
    description: 'Solfeggio 639 Hz: Connection, relationships, harmony, and communication',
    complexity: 7
  },
  
  'solfeggio-741': {
    oscillators: [
      // 741 Hz - Expression, solutions, creativity
      { frequency: 741, amplitude: 1.0, waveform: 'sine', phase: 0 },
      { frequency: 1482, amplitude: 0.9, waveform: 'sine', phase: Math.PI / 8 },
      { frequency: 2223, amplitude: 0.7, waveform: 'sine', phase: Math.PI / 4 },
      { frequency: 2964, amplitude: 0.5, waveform: 'sine', phase: Math.PI * 3 / 8 },
      { frequency: 3705, amplitude: 0.3, waveform: 'sine', phase: Math.PI / 2 }
    ],
    carrierFrequency: 741,
    modulatorFrequency: 123.5,
    modulationIndex: 2.4,
    description: 'Solfeggio 741 Hz: Expression, solutions, creativity, and problem-solving',
    complexity: 7
  },
  
  'solfeggio-852': {
    oscillators: [
      // 852 Hz - Awakening intuition, returning to spiritual order
      { frequency: 852, amplitude: 1.0, waveform: 'sine', phase: 0 },
      { frequency: 1704, amplitude: 0.9, waveform: 'sine', phase: Math.PI / 9 },
      { frequency: 2556, amplitude: 0.7, waveform: 'sine', phase: Math.PI * 2 / 9 },
      { frequency: 3408, amplitude: 0.5, waveform: 'sine', phase: Math.PI / 3 },
      { frequency: 4260, amplitude: 0.3, waveform: 'sine', phase: Math.PI * 4 / 9 }
    ],
    carrierFrequency: 852,
    modulatorFrequency: 142,
    modulationIndex: 2.6,
    description: 'Solfeggio 852 Hz: Awakening intuition, returning to spiritual order, and inner vision',
    complexity: 8
  },
  
  'solfeggio-963': {
    oscillators: [
      // 963 Hz - Crown chakra, connection to source, oneness
      { frequency: 963, amplitude: 1.0, waveform: 'sine', phase: 0 },
      { frequency: 1926, amplitude: 0.9, waveform: 'sine', phase: Math.PI / 10 },
      { frequency: 2889, amplitude: 0.7, waveform: 'sine', phase: Math.PI / 5 },
      { frequency: 3852, amplitude: 0.5, waveform: 'sine', phase: Math.PI * 3 / 10 },
      { frequency: 4815, amplitude: 0.3, waveform: 'sine', phase: Math.PI * 2 / 5 }
    ],
    carrierFrequency: 963,
    modulatorFrequency: 160.5,
    modulationIndex: 2.7,
    description: 'Solfeggio 963 Hz: Crown chakra, connection to source, oneness, and unity consciousness',
    complexity: 8
  },
  
  // ============================================================================
  // ADDITIONAL SACRED GEOMETRY PATTERNS
  // ============================================================================
  
  'fruit-of-life': {
    oscillators: [
      // Fruit of Life: 13 circles from Flower of Life
      { frequency: 264, amplitude: 0.9, waveform: 'sine', phase: 0 },
      { frequency: 396, amplitude: 0.9, waveform: 'sine', phase: Math.PI / 13 },
      { frequency: 528, amplitude: 1.0, waveform: 'sine', phase: Math.PI * 2 / 13 },
      { frequency: 660, amplitude: 0.8, waveform: 'sine', phase: Math.PI * 3 / 13 },
      { frequency: 792, amplitude: 0.8, waveform: 'sine', phase: Math.PI * 4 / 13 },
      { frequency: 924, amplitude: 0.7, waveform: 'sine', phase: Math.PI * 5 / 13 },
      { frequency: 1056, amplitude: 0.7, waveform: 'sine', phase: Math.PI * 6 / 13 },
      { frequency: 1188, amplitude: 0.6, waveform: 'sine', phase: Math.PI * 7 / 13 },
      { frequency: 1320, amplitude: 0.6, waveform: 'sine', phase: Math.PI * 8 / 13 },
      { frequency: 1452, amplitude: 0.5, waveform: 'sine', phase: Math.PI * 9 / 13 },
      { frequency: 1584, amplitude: 0.5, waveform: 'sine', phase: Math.PI * 10 / 13 },
      { frequency: 1716, amplitude: 0.4, waveform: 'sine', phase: Math.PI * 11 / 13 },
      { frequency: 1848, amplitude: 0.4, waveform: 'sine', phase: Math.PI * 12 / 13 }
    ],
    carrierFrequency: 528,
    modulatorFrequency: 40.6, // 13-fold modulation
    modulationIndex: 2.8,
    description: 'Fruit of Life: 13 circles containing the blueprint for all atomic and molecular structures',
    complexity: 9
  },
  
  'merkaba': {
    oscillators: [
      // Merkaba: Two interlocking tetrahedra (star tetrahedron)
      // 8 vertices, counter-rotating fields
      { frequency: 144, amplitude: 0.9, waveform: 'sine', phase: 0 },
      { frequency: 216, amplitude: 0.9, waveform: 'sine', phase: Math.PI / 8 },
      { frequency: 288, amplitude: 1.0, waveform: 'sine', phase: Math.PI / 4 },
      { frequency: 360, amplitude: 0.8, waveform: 'sine', phase: Math.PI * 3 / 8 },
      { frequency: 432, amplitude: 0.8, waveform: 'sine', phase: Math.PI / 2 },
      { frequency: 504, amplitude: 0.7, waveform: 'sine', phase: Math.PI * 5 / 8 },
      { frequency: 576, amplitude: 0.7, waveform: 'sine', phase: Math.PI * 3 / 4 },
      { frequency: 648, amplitude: 0.6, waveform: 'sine', phase: Math.PI * 7 / 8 },
      // Counter-rotation frequencies (34/21 Fibonacci ratio)
      { frequency: 233.14, amplitude: 0.5, waveform: 'sine', phase: Math.PI, lfoFrequency: 0.34, lfoDepth: 0.2 },
      { frequency: 144, amplitude: 0.4, waveform: 'sine', phase: 0, lfoFrequency: 0.21, lfoDepth: 0.2 }
    ],
    carrierFrequency: 432,
    modulatorFrequency: 18, // 8-fold modulation
    modulationIndex: 3.0,
    description: 'Merkaba: Two interlocking tetrahedra creating 8-vertex star, counter-rotating at 34/21 ratio (Fibonacci)',
    complexity: 9
  },
  
  'octagon': {
    oscillators: [
      // 8-fold symmetry
      { frequency: 240, amplitude: 0.9, waveform: 'sine', phase: 0 },
      { frequency: 360, amplitude: 0.8, waveform: 'sine', phase: Math.PI / 4 },
      { frequency: 480, amplitude: 1.0, waveform: 'sine', phase: Math.PI / 2 },
      { frequency: 600, amplitude: 0.7, waveform: 'sine', phase: Math.PI * 3 / 4 },
      { frequency: 720, amplitude: 0.6, waveform: 'sine', phase: Math.PI },
      { frequency: 840, amplitude: 0.5, waveform: 'sine', phase: Math.PI * 5 / 4 },
      { frequency: 960, amplitude: 0.4, waveform: 'sine', phase: Math.PI * 3 / 2 },
      { frequency: 1080, amplitude: 0.3, waveform: 'sine', phase: Math.PI * 7 / 4 }
    ],
    carrierFrequency: 480,
    modulatorFrequency: 60, // 8-fold modulation
    modulationIndex: 2.3,
    description: 'Octagon: 8-fold symmetry pattern creating octagonal nodal structures',
    complexity: 7
  },
  
  'dodecagon': {
    oscillators: [
      // 12-fold symmetry
      { frequency: 220, amplitude: 0.9, waveform: 'sine', phase: 0 },
      { frequency: 330, amplitude: 0.8, waveform: 'sine', phase: Math.PI / 6 },
      { frequency: 440, amplitude: 1.0, waveform: 'sine', phase: Math.PI / 3 },
      { frequency: 550, amplitude: 0.7, waveform: 'sine', phase: Math.PI / 2 },
      { frequency: 660, amplitude: 0.6, waveform: 'sine', phase: Math.PI * 2 / 3 },
      { frequency: 770, amplitude: 0.5, waveform: 'sine', phase: Math.PI * 5 / 6 },
      { frequency: 880, amplitude: 0.4, waveform: 'sine', phase: Math.PI },
      { frequency: 990, amplitude: 0.3, waveform: 'sine', phase: Math.PI * 7 / 6 },
      { frequency: 1100, amplitude: 0.3, waveform: 'sine', phase: Math.PI * 4 / 3 },
      { frequency: 1210, amplitude: 0.2, waveform: 'sine', phase: Math.PI * 3 / 2 },
      { frequency: 1320, amplitude: 0.2, waveform: 'sine', phase: Math.PI * 5 / 3 },
      { frequency: 1430, amplitude: 0.2, waveform: 'sine', phase: Math.PI * 11 / 6 }
    ],
    carrierFrequency: 440,
    modulatorFrequency: 36.67, // 12-fold modulation
    modulationIndex: 2.5,
    description: 'Dodecagon: 12-fold symmetry pattern with complex nodal structures',
    complexity: 8
  },
  
  'water-cymatics': {
    oscillators: [
      // Water cymatics: Lower frequencies, fluid dynamics
      { frequency: 50, amplitude: 1.0, waveform: 'sine', phase: 0 },
      { frequency: 100, amplitude: 0.9, waveform: 'sine', phase: Math.PI / 5 },
      { frequency: 150, amplitude: 0.8, waveform: 'sine', phase: Math.PI * 2 / 5 },
      { frequency: 200, amplitude: 0.7, waveform: 'sine', phase: Math.PI * 3 / 5 },
      { frequency: 250, amplitude: 0.6, waveform: 'sine', phase: Math.PI * 4 / 5 },
      // Fluid harmonics
      { frequency: 75, amplitude: 0.5, waveform: 'triangle', lfoFrequency: 0.1, lfoDepth: 0.2 },
      { frequency: 125, amplitude: 0.4, waveform: 'sine', phase: Math.PI, lfoFrequency: 0.15, lfoDepth: 0.15 }
    ],
    carrierFrequency: 100,
    modulatorFrequency: 10, // Slow modulation for fluid patterns
    modulationIndex: 1.5,
    description: 'Water cymatics: Lower frequency patterns simulating fluid dynamics and water surface waves',
    complexity: 6
  },
  
  'snowflake': {
    oscillators: [
      // Snowflake: 6-fold symmetry with branching
      { frequency: 180, amplitude: 1.0, waveform: 'sine', phase: 0 },
      { frequency: 270, amplitude: 0.9, waveform: 'sine', phase: Math.PI / 6 },
      { frequency: 360, amplitude: 0.9, waveform: 'sine', phase: Math.PI / 3 },
      { frequency: 450, amplitude: 0.8, waveform: 'sine', phase: Math.PI / 2 },
      { frequency: 540, amplitude: 0.7, waveform: 'sine', phase: Math.PI * 2 / 3 },
      { frequency: 630, amplitude: 0.6, waveform: 'sine', phase: Math.PI * 5 / 6 },
      // Branching frequencies
      { frequency: 90, amplitude: 0.5, waveform: 'triangle', lfoFrequency: 0.2, lfoDepth: 0.15 },
      { frequency: 720, amplitude: 0.4, waveform: 'sine', phase: Math.PI }
    ],
    carrierFrequency: 360,
    modulatorFrequency: 60, // 6-fold modulation
    modulationIndex: 2.4,
    description: 'Snowflake: 6-fold symmetry with branching patterns creating crystalline structures',
    complexity: 7
  },
  
  'yin-yang': {
    oscillators: [
      // Yin-Yang: Dual opposing forces in harmony
      { frequency: 200, amplitude: 1.0, waveform: 'sine', phase: 0 }, // Yang (active)
      { frequency: 200, amplitude: 1.0, waveform: 'sine', phase: Math.PI }, // Yin (receptive)
      // Harmonic frequencies
      { frequency: 400, amplitude: 0.7, waveform: 'sine', phase: Math.PI / 2 },
      { frequency: 600, amplitude: 0.5, waveform: 'sine', phase: Math.PI * 3 / 2 },
      // Balance frequencies
      { frequency: 300, amplitude: 0.6, waveform: 'sine', phase: Math.PI / 4 },
      { frequency: 500, amplitude: 0.4, waveform: 'sine', phase: Math.PI * 5 / 4 }
    ],
    carrierFrequency: 200,
    modulatorFrequency: 33.33,
    modulationIndex: 2.0,
    description: 'Yin-Yang: Dual opposing frequencies in perfect balance creating harmonious circular patterns',
    complexity: 6
  }
};

/**
 * Get cymatic frequency configuration for a pattern
 */
export function getCymaticFrequency(patternId: string): CymaticFrequencyConfig | null {
  // Backward compatibility: egg-of-life was consolidated into seed-of-life
  const normalizedId = patternId === 'egg-of-life' ? 'seed-of-life' : patternId;
  return CYMATIC_FREQUENCIES[normalizedId] || null;
}

/**
 * Get all oscillator frequencies for a pattern
 */
export function getAllOscillators(patternId: string): CymaticOscillator[] {
  const config = getCymaticFrequency(patternId);
  if (!config) return [];
  
  return config.oscillators || [];
}

/**
 * Get primary frequency for display (first oscillator or carrier)
 */
export function getPrimaryFrequency(patternId: string): number {
  const config = getCymaticFrequency(patternId);
  if (!config) return 0;
  
  if (config.oscillators && config.oscillators.length > 0) {
    return config.oscillators[0].frequency;
  }
  return config.carrierFrequency || 0;
}

/**
 * Get frequency description for display
 */
export function getFrequencyDescription(patternId: string): string {
  const config = getCymaticFrequency(patternId);
  return config?.description || 'No cymatic frequency mapping available';
}

/**
 * Get complexity level for a pattern
 */
export function getComplexity(patternId: string): number {
  const config = getCymaticFrequency(patternId);
  return config?.complexity || 0;
}

